import { motion, useReducedMotion } from 'framer-motion'
import type { ComponentType, ReactNode } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { cn } from '@/utils/cn'

/** Color axis for primitives that also appear on the inverted (bg-foreground) band. */
type Tone = 'default' | 'inverse'

/** Scroll-triggered rise-and-fade reveal; renders statically under reduced motion. */
export function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode
  delay?: number
  className?: string
}) {
  const reduced = useReducedMotion()
  return (
    <motion.div
      className={className}
      initial={reduced ? false : { opacity: 0, y: 20 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  )
}

/** Numbered editorial kicker: `01 —— LABEL`. */
export function SectionLabel({
  index,
  children,
  tone = 'default',
}: {
  index: string
  children: ReactNode
  tone?: Tone
}) {
  const inverse = tone === 'inverse'
  return (
    <div className="flex items-center gap-4">
      <span
        className={cn(
          'font-mono text-2xs tracking-[0.25em]',
          inverse ? 'text-background' : 'text-primary-strong'
        )}
      >
        {index}
      </span>
      <span
        className={cn('h-px w-10', inverse ? 'bg-background/30' : 'bg-border')}
        aria-hidden="true"
      />
      <span
        className={cn(
          'font-mono text-2xs uppercase tracking-[0.3em]',
          inverse ? 'text-background/60' : 'text-muted-foreground'
        )}
      >
        {children}
      </span>
    </div>
  )
}

export function SectionIntro({
  index,
  label,
  title,
  lede,
}: {
  index: string
  label: string
  title: ReactNode
  lede: string
}) {
  return (
    <Reveal className="max-w-3xl space-y-6">
      <SectionLabel index={index}>{label}</SectionLabel>
      <h2 className="font-brand text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
        {title}
      </h2>
      <p className="text-lg leading-relaxed text-muted-foreground">{lede}</p>
    </Reveal>
  )
}

interface FeaturePoint {
  title: string
  description: string
}

export function FeatureList({
  items,
  className,
  tone = 'default',
}: {
  items: FeaturePoint[]
  className?: string
  tone?: Tone
}) {
  const inverse = tone === 'inverse'
  return (
    <div className={cn('grid gap-x-10 gap-y-8 sm:grid-cols-2', className)}>
      {items.map((item, i) => (
        <Reveal
          key={item.title}
          delay={i * 0.06}
          className={cn('border-l pl-5', inverse ? 'border-background/25' : 'border-border')}
        >
          <h3 className={cn('text-sm font-semibold tracking-wide', !inverse && 'text-foreground')}>
            {item.title}
          </h3>
          <p
            className={cn(
              'mt-1.5 text-sm leading-relaxed',
              inverse ? 'text-background/60' : 'text-muted-foreground'
            )}
          >
            {item.description}
          </p>
        </Reveal>
      ))}
    </div>
  )
}

/** Full-bleed section band with a hairline top rule and centered content column. */
export function Section({
  id,
  className,
  children,
}: {
  id: string
  className?: string
  children: ReactNode
}) {
  return (
    <section id={id} className={cn('scroll-mt-24 border-t border-border/60', className)}>
      <div className="mx-auto max-w-6xl px-6 py-24 md:py-28">{children}</div>
    </section>
  )
}

export function FormatChip({ children }: { children: ReactNode }) {
  return (
    <Badge
      variant="outline"
      className="h-auto px-3.5 py-1.5 font-mono text-2xs tracking-wider text-foreground/80"
    >
      {children}
    </Badge>
  )
}

interface SourceGroup {
  title: string
  /** Jurisdiction or scope marker, right-aligned against the title. */
  scope: string
  description: string
  /** The named sources themselves — the concrete part readers scan for. */
  sources: string[]
}

/** Grid of named source classes: title + scope marker, body, then the roster. */
export function SourceGroupGrid({ groups }: { groups: SourceGroup[] }) {
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {groups.map((group, i) => (
        <Reveal key={group.title} delay={i * 0.06}>
          <Card className="flex h-full flex-col rounded-2xl p-7 md:p-8">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="font-brand text-xl font-semibold tracking-tight text-card-foreground">
                {group.title}
              </h3>
              <span className="shrink-0 font-mono text-2xs tracking-[0.2em] text-primary-strong">
                {group.scope}
              </span>
            </div>
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
              {group.description}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {group.sources.map(source => (
                <FormatChip key={source}>{source}</FormatChip>
              ))}
            </div>
          </Card>
        </Reveal>
      ))}
    </div>
  )
}

interface ShowcaseItem {
  kicker: string
  title: string
  body: string
  visual: ComponentType<{ className?: string }>
}

/** Two-up card grid with a kicker, display title, body, and bottom illustration. */
export function ShowcaseCardGrid({ items }: { items: ShowcaseItem[] }) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {items.map((item, i) => (
        <Reveal key={item.title} delay={i * 0.08}>
          <Card className="flex h-full flex-col rounded-2xl p-8 md:p-10">
            <span className="font-mono text-2xs uppercase tracking-[0.3em] text-primary-strong">
              {item.kicker}
            </span>
            <h3 className="mt-3 font-brand text-2xl font-semibold tracking-tight text-card-foreground">
              {item.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
            <item.visual className="mt-auto w-full pt-8" />
          </Card>
        </Reveal>
      ))}
    </div>
  )
}
